import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Menu, Skeleton, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Job, Line, TranscriptMutationResponse } from '@nelnela/contracts';
import { api, ApiError } from '../../lib/api';
import { useStudy } from '../../app/study';
import {
  applyMutation,
  canMerge as canMergeAt,
  classifyEditError,
  describeUndo,
  fmtTimeMs,
  incomingLinesAction,
  insertDefaults,
  inverseOf,
  jobLockLine,
  jobLocks,
  jobNoun,
  jobTransition,
  knownSpeakers,
  missingTranslations,
  nudge,
  pickAnchor,
  pushUndo,
  replaceAll,
  sortLines,
  splitAtCursor,
  undoableCount,
  undoDisabledReason,
  type LinePatch,
  type MutationOp,
  type UndoEntry,
} from '../../lib/editor';
import { LineEditor, type TimeField } from './LineEditor';
import { IconMore } from './icons';

export interface TranscriptEditorProps {
  transcriptId: string;
  video: {
    id: string;
    visibility: 'shared' | 'owner';
    externalId: string;
    durationMs: number | null;
  };
  /** initial from the Watch query; later values are adopted when their revision is newer */
  lines: Line[];
  revision: number;
  /** owner of a private video, or curator (the integrator computes) */
  canEdit: boolean;
  /** the integrator's useJob state — a queued/running job locks editing */
  runningJob: Job | null;
  currentMs: number;
  activeIdx: number;
  onSeek: (ms: number) => void;
  /** after every commit — keep Watch's line list / engine in sync */
  onLinesChanged: (lines: Line[], revision: number) => void;
  /** the integrator starts the job and shows the pill; the editor just asks */
  onStartJob: (kind: 'cleanup' | 'translate') => void;
  /** the integrator refetches the video; the fresh lines/revision arrive through props */
  onRequestReload: () => void;
  /** "Done" */
  onExit: () => void;
  style?: React.CSSProperties;
}

type Planned =
  | { op: MutationOp; call: (revision: number) => Promise<TranscriptMutationResponse> }
  | { error: string; lineId?: string };

const STATUS_TTL_MS = 8000;
const NOTE_TTL_MS = 6000;

/**
 * F5 — the subtitle editor surface, a drop-in for the transcript column (fills its container,
 * owns its scroll box). Per-line commits with the transcript `revision` threaded through every
 * call (M3-HANDOFF: stable ids, no save-all); a 20-deep undo stack of inverse ops; snapshots →
 * restore; export SRT (private videos); "Run cleanup" / "Translate missing" ask the integrator to
 * start jobs; a running job locks the rows; a job finishing under an in-progress edit shows the
 * conflict banner from the local app (Workspace.jsx:463-478) inline, never as a modal.
 */
export function TranscriptEditor(props: TranscriptEditorProps) {
  const { transcriptId, video, canEdit, runningJob, activeIdx, onSeek, onStartJob, onExit, style } =
    props;
  // Callbacks the integrator may recreate per render — read through a ref so effects and the
  // serial queue never go stale or re-run because of them.
  const cbRef = useRef(props);
  cbRef.current = props;

  const desktop =
    useMediaQuery('(min-width: 62em)', true, { getInitialValueInEffect: false }) ?? true;
  const { entitled } = useStudy();
  const queryClient = useQueryClient();
  const speakerListId = useId();

  // ------------------------------------------------ local transcript state
  const [lines, setLines] = useState<Line[]>(() => sortLines(props.lines));
  const linesRef = useRef(lines);
  const [revision, setRevision] = useState(props.revision);
  const revisionRef = useRef(props.revision);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const commitLines = useCallback((next: Line[], rev: number, opts: { echo?: boolean } = {}) => {
    linesRef.current = next;
    revisionRef.current = rev;
    if (mountedRef.current) {
      setLines(next);
      setRevision(rev);
    }
    if (opts.echo !== false) cbRef.current.onLinesChanged(next, rev);
  }, []);

  // ------------------------------------------------ ui state
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dirtyId, setDirtyId] = useState<string | null>(null);
  const dirtyRef = useRef<string | null>(null);
  useEffect(() => {
    dirtyRef.current = dirtyId;
  }, [dirtyId]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<{ lineId: string; message: string } | null>(null);
  const [rowNote, setRowNote] = useState<{ lineId: string; message: string } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [undo, setUndo] = useState<UndoEntry[]>([]);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const [exportOpts, setExportOpts] = useState({ speakers: false, english: false });
  /** a 409-with-job answer we learned about before the integrator's poller did */
  const [localJob, setLocalJob] = useState<Job | null>(null);
  /** lines that arrived while an edit was in progress — held until the learner decides */
  const incomingRef = useRef<{ lines: Line[]; revision: number } | null>(null);
  const adoptOnArrivalRef = useRef(false);
  const lastFinishedRef = useRef<string | null>(null);
  const [banner, setBanner] = useState<{ jobKind: string | null } | null>(null);

  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(null), STATUS_TTL_MS);
    return () => clearTimeout(t);
  }, [status]);
  useEffect(() => {
    if (!rowNote) return;
    const t = setTimeout(() => setRowNote(null), NOTE_TTL_MS);
    return () => clearTimeout(t);
  }, [rowNote]);

  const onDirty = useCallback((lineId: string, dirty: boolean) => {
    setDirtyId((cur) => (dirty ? lineId : cur === lineId ? null : cur));
  }, []);

  // ------------------------------------------------ locks
  const lockingJob = jobLocks(runningJob) ? runningJob : jobLocks(localJob) ? localJob : null;
  const locked = lockingJob !== null;
  const disabled = locked || !canEdit;
  const disabledReason = !canEdit
    ? video.visibility === 'shared'
      ? 'Shared transcripts are edited by curators'
      : 'Only the owner can edit this transcript'
    : locked
      ? jobLockLine(lockingJob)
      : null;

  // ------------------------------------------------ adopt fresh lines from props
  const adoptIncoming = useCallback(
    (incoming: { lines: Line[]; revision: number }) => {
      incomingRef.current = null;
      adoptOnArrivalRef.current = false;
      lastFinishedRef.current = null;
      setBanner(null);
      setLocalJob(null);
      commitLines(sortLines(incoming.lines), incoming.revision, { echo: false });
    },
    [commitLines],
  );

  useEffect(() => {
    const action = incomingLinesAction(
      props.revision,
      revisionRef.current,
      dirtyId !== null,
      adoptOnArrivalRef.current,
    );
    if (action === 'ignore') {
      // a stashed copy older than what we already have is moot
      if (incomingRef.current && incomingRef.current.revision <= revisionRef.current) {
        incomingRef.current = null;
        setBanner(null);
      }
      return;
    }
    if (action === 'adopt') {
      adoptIncoming({ lines: props.lines, revision: props.revision });
      return;
    }
    incomingRef.current = { lines: props.lines, revision: props.revision };
    setBanner((b) => b ?? { jobKind: lastFinishedRef.current });
  }, [props.lines, props.revision, dirtyId, adoptIncoming]);

  // ------------------------------------------------ job finished while editing (Workspace.jsx:463-478)
  const prevJobRef = useRef<Job | null>(runningJob);
  useEffect(() => {
    const t = jobTransition(prevJobRef.current, runningJob);
    prevJobRef.current = runningJob;
    setLocalJob(null);
    if (!t) return;
    if (!t.ok) {
      setStatus(`${jobNoun(t.jobKind)} failed — the transcript is unchanged`);
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ['snapshots', transcriptId] });
    lastFinishedRef.current = t.jobKind;
    if (dirtyRef.current) {
      setBanner({ jobKind: t.jobKind });
    } else {
      adoptOnArrivalRef.current = true;
      cbRef.current.onRequestReload();
    }
  }, [runningJob, queryClient, transcriptId]);

  const bannerReload = useCallback(() => {
    setResetKey((k) => k + 1); // drop drafts
    if (incomingRef.current) adoptIncoming(incomingRef.current);
    else {
      adoptOnArrivalRef.current = true;
      setBanner(null);
      cbRef.current.onRequestReload();
    }
  }, [adoptIncoming]);
  const bannerKeep = useCallback(() => setBanner(null), []);

  // ------------------------------------------------ the serial mutation queue
  // Every server op runs one at a time and reads the revision when it starts, so a blur-commit
  // followed by a split (mousedown blurs, click splits) never races on `revision`.
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const runMutation = useCallback(
    (
      plan: (lines: Line[]) => Planned,
      opts: { skipUndo?: boolean } = {},
    ): Promise<TranscriptMutationResponse | null> => {
      let resolve!: (r: TranscriptMutationResponse | null) => void;
      const done = new Promise<TranscriptMutationResponse | null>((r) => (resolve = r));
      const task = async () => {
        // The learner chose "keep editing" over a job's result: their next commit goes against
        // the fresh transcript (a translate keeps ids; a cleanup replaced them — the row is gone).
        if (incomingRef.current) adoptIncoming(incomingRef.current);
        const p = plan(linesRef.current);
        if ('error' in p) {
          if (p.lineId) setRowError({ lineId: p.lineId, message: p.error });
          else setStatus(p.error);
          resolve(null);
          return;
        }
        const { op, call } = p;
        if (op.lineId && !linesRef.current.some((l) => l.id === op.lineId)) {
          setStatus('That line was replaced by the job — the edit wasn’t applied');
          resolve(null);
          return;
        }
        setBusy(true);
        setBusyId(op.lineId ?? null);
        try {
          const resp = await call(revisionRef.current);
          const before = linesRef.current;
          const after = applyMutation(before, resp, op);
          if (!opts.skipUndo) {
            const inv = inverseOf(op, before, after);
            if (inv) setUndo((s) => pushUndo(s, inv));
            else if (op.kind === 'split' || op.kind === 'merge') {
              const barrierOp = op.kind;
              setUndo((s) => pushUndo(s, { kind: 'barrier', op: barrierOp }));
            }
          }
          commitLines(after, resp.revision);
          if (op.lineId) setRowError((e) => (e && e.lineId === op.lineId ? null : e));
          resolve(resp);
        } catch (e) {
          const cls = classifyEditError(e instanceof ApiError ? e.problem : null);
          switch (cls.kind) {
            case 'stale':
              setStatus('Transcript changed elsewhere — reloaded');
              adoptOnArrivalRef.current = true;
              cbRef.current.onRequestReload();
              break;
            case 'job':
              if (cls.job) setLocalJob(cls.job);
              setStatus(jobLockLine(cls.job));
              cbRef.current.onRequestReload(); // lets the integrator's poller pick the job up
              break;
            case 'gone':
              setStatus('That line no longer exists — reloaded');
              adoptOnArrivalRef.current = true;
              cbRef.current.onRequestReload();
              break;
            case 'forbidden':
              setStatus(cls.message);
              break;
            case 'validation':
            case 'budget':
            case 'other':
              if (op.lineId) setRowError({ lineId: op.lineId, message: cls.message });
              else setStatus(cls.message);
              break;
          }
          resolve(null);
        } finally {
          if (mountedRef.current) {
            setBusy(false);
            setBusyId(null);
          }
        }
      };
      chainRef.current = chainRef.current.then(task, task);
      return done;
    },
    [adoptIncoming, commitLines],
  );

  const guard = useCallback((): boolean => {
    if (!disabled) return true;
    if (disabledReason) setStatus(disabledReason);
    return false;
  }, [disabled, disabledReason]);

  // ------------------------------------------------ row callbacks (stable — rows are memo'd)
  const onCommit = useCallback(
    (lineId: string, patch: LinePatch) => {
      if (!guard()) return;
      void runMutation(() => ({
        op: { kind: 'update', lineId },
        call: (rev) => api.lineUpdate(transcriptId, lineId, { revision: rev, ...patch }),
      }));
    },
    [guard, runMutation, transcriptId],
  );

  const onNudge = useCallback(
    (lineId: string, field: TimeField, delta: number) => {
      if (!guard()) return;
      void runMutation((ls) => {
        const l = ls.find((x) => x.id === lineId);
        if (!l) return { error: 'That line is gone', lineId };
        const value = nudge(l[field], delta);
        return {
          op: { kind: 'update', lineId },
          call: (rev) => api.lineUpdate(transcriptId, lineId, { revision: rev, [field]: value }),
        };
      });
    },
    [guard, runMutation, transcriptId],
  );

  const onSetToPlayhead = useCallback(
    (lineId: string, field: TimeField) => {
      if (!guard()) return;
      const value = Math.max(0, Math.round(cbRef.current.currentMs));
      void runMutation(() => ({
        op: { kind: 'update', lineId },
        call: (rev) => api.lineUpdate(transcriptId, lineId, { revision: rev, [field]: value }),
      }));
    },
    [guard, runMutation, transcriptId],
  );

  const onSplit = useCallback(
    (lineId: string, atChar: number) => {
      if (!guard()) return;
      void runMutation((ls) => {
        const l = ls.find((x) => x.id === lineId);
        if (!l) return { error: 'That line is gone', lineId };
        const v = splitAtCursor(l.text, atChar);
        if (!v.ok) return { error: v.reason, lineId };
        return {
          op: { kind: 'split', lineId },
          call: (rev) => api.lineSplit(transcriptId, lineId, { revision: rev, atChar }),
        };
      });
    },
    [guard, runMutation, transcriptId],
  );

  const onMerge = useCallback(
    (lineId: string) => {
      if (!guard()) return;
      void runMutation((ls) => {
        const i = ls.findIndex((x) => x.id === lineId);
        if (i === -1) return { error: 'That line is gone', lineId };
        if (!canMergeAt(ls, i))
          return { error: 'This is the last line — nothing to merge', lineId };
        const next = ls[i + 1];
        return {
          op: { kind: 'merge', lineId, removedIds: [next.id] },
          call: (rev) =>
            api.lineMerge(transcriptId, lineId, { revision: rev, withNextLineId: next.id }),
        };
      });
    },
    [guard, runMutation, transcriptId],
  );

  const onInsertAfter = useCallback(
    (lineId: string) => {
      if (!guard()) return;
      void runMutation((ls) => {
        const i = ls.findIndex((x) => x.id === lineId);
        if (i === -1) return { error: 'That line is gone', lineId };
        const after = ls[i];
        const d = insertDefaults(after, ls[i + 1] ?? null, video.durationMs);
        if (!d.ok) return { error: d.reason, lineId };
        return {
          op: { kind: 'insert' },
          call: (rev) =>
            api.lineInsert(transcriptId, {
              revision: rev,
              afterLineId: lineId,
              startMs: d.startMs,
              endMs: d.endMs,
              text: '…',
              speaker: after.speaker,
            }),
        };
      }).then((resp) => {
        const created = resp?.lines[0];
        if (created && mountedRef.current) setExpandedId(created.id);
      });
    },
    [guard, runMutation, transcriptId, video.durationMs],
  );

  const onDelete = useCallback(
    (lineId: string) => {
      if (!guard()) return;
      setExpandedId((cur) => (cur === lineId ? null : cur));
      void runMutation(() => ({
        op: { kind: 'delete', lineId },
        call: (rev) => api.lineDelete(transcriptId, lineId, rev),
      }));
    },
    [guard, runMutation, transcriptId],
  );

  const onExpand = useCallback((lineId: string) => setExpandedId(lineId), []);
  const onCollapse = useCallback(() => setExpandedId(null), []);

  // ------------------------------------------------ undo
  const undoReason = undoDisabledReason(undo);
  const undoCount = undoableCount(undo);
  const doUndo = useCallback(() => {
    if (!guard()) return;
    const top = undo[undo.length - 1];
    if (!top || top.kind === 'barrier') return;
    setUndo((s) => s.slice(0, -1));
    switch (top.kind) {
      case 'update':
        void runMutation(
          () => ({
            op: { kind: 'update', lineId: top.lineId },
            call: (rev) =>
              api.lineUpdate(transcriptId, top.lineId, { revision: rev, ...top.patch }),
          }),
          { skipUndo: true },
        );
        break;
      case 'delete':
        void runMutation(
          () => ({
            op: { kind: 'delete', lineId: top.lineId },
            call: (rev) => api.lineDelete(transcriptId, top.lineId, rev),
          }),
          { skipUndo: true },
        );
        break;
      case 'insert':
        void runMutation(
          () => ({
            op: { kind: 'insert' },
            call: async (rev) => {
              const r = await api.lineInsert(transcriptId, {
                revision: rev,
                afterLineId: top.afterLineId,
                startMs: top.line.startMs,
                endMs: top.line.endMs,
                text: top.line.text,
                speaker: top.line.speaker,
              });
              const created = r.lines[0];
              if (!created || !top.line.translation) return r;
              // LineInsertRequest carries no translation — patch it onto the new row
              const r2 = await api.lineUpdate(transcriptId, created.id, {
                revision: r.revision,
                translation: top.line.translation,
              });
              return {
                revision: r2.revision,
                lines: [...r.lines.filter((l) => l.id !== created.id), ...r2.lines],
              };
            },
          }),
          { skipUndo: true },
        ).then((resp) => {
          const created = resp?.lines.find((l) => l.text === top.line.text) ?? resp?.lines[0];
          if (created && mountedRef.current)
            setRowNote({ lineId: created.id, message: 'restored — this line has a new id' });
        });
        break;
    }
  }, [guard, undo, runMutation, transcriptId]);

  // ------------------------------------------------ snapshots
  const restore = useCallback(
    (snapshotId: string) => {
      if (!guard()) return;
      setSnapOpen(false);
      setExpandedId(null);
      setResetKey((k) => k + 1);
      const task = async () => {
        setBusy(true);
        try {
          const resp = await api.transcriptRestore(transcriptId, snapshotId);
          if (resp.lines.length === 0) {
            // a restore answers with the whole transcript; an empty echo means "go fetch"
            adoptOnArrivalRef.current = true;
            cbRef.current.onRequestReload();
          } else {
            commitLines(replaceAll(resp), resp.revision);
          }
          setUndo([]);
          setStatus('Snapshot restored');
        } catch (e) {
          const cls = classifyEditError(e instanceof ApiError ? e.problem : null);
          if (cls.kind === 'job') {
            if (cls.job) setLocalJob(cls.job);
            setStatus(jobLockLine(cls.job));
          } else if (cls.kind === 'stale') {
            adoptOnArrivalRef.current = true;
            cbRef.current.onRequestReload();
            setStatus('Transcript changed elsewhere — reloaded');
          } else setStatus('message' in cls ? cls.message : 'Couldn’t restore that snapshot');
        } finally {
          if (mountedRef.current) setBusy(false);
          void queryClient.invalidateQueries({ queryKey: ['snapshots', transcriptId] });
        }
      };
      chainRef.current = chainRef.current.then(task, task);
    },
    [guard, transcriptId, commitLines, queryClient],
  );

  // ------------------------------------------------ derived
  const speakers = useMemo(() => knownSpeakers(lines), [lines]);
  const missing = useMemo(() => missingTranslations(lines), [lines]);
  const activeId = props.lines[activeIdx]?.id ?? null;
  const activeLocalIdx = useMemo(
    () => (activeId ? lines.findIndex((l) => l.id === activeId) : -1),
    [lines, activeId],
  );

  // ------------------------------------------------ scroll box + anchoring
  const containerRef = useRef<HTMLDivElement>(null);
  // Snapshot the anchor row's offset in the render that changes the expanded row (rows change
  // height), restore it before paint — the hooks form of getSnapshotBeforeUpdate
  // (Transcript.jsx:119-166). The row being opened/closed is preferred, then the playing line.
  const prevExpandedRef = useRef<string | null>(expandedId);
  const anchorRef = useRef<{ idx: number; top: number } | null>(null);
  if (prevExpandedRef.current !== expandedId) {
    const c = containerRef.current;
    if (c) {
      const cTop = c.getBoundingClientRect().top;
      const rows = Array.from(c.querySelectorAll<HTMLElement>('[data-idx]')).map((n) => {
        const r = n.getBoundingClientRect();
        return { idx: Number(n.dataset.idx), top: r.top - cTop, bottom: r.bottom - cTop };
      });
      const idxOf = (id: string | null) => (id ? lines.findIndex((l) => l.id === id) : -1);
      const preferred = [idxOf(expandedId), idxOf(prevExpandedRef.current), activeLocalIdx].filter(
        (i) => i >= 0,
      );
      anchorRef.current = pickAnchor(rows, c.clientHeight, preferred);
    }
    prevExpandedRef.current = expandedId;
  }
  useLayoutEffect(() => {
    const a = anchorRef.current;
    anchorRef.current = null;
    const c = containerRef.current;
    if (!a || !c) return;
    const el = c.querySelector<HTMLElement>(`[data-idx="${a.idx}"]`);
    if (el) c.scrollTop += el.getBoundingClientRect().top - c.getBoundingClientRect().top - a.top;
  }, [expandedId]);

  // On mount, land where the read view was: the playing line at the centre of the box.
  useLayoutEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const el = c.querySelector<HTMLElement>('[data-active="true"]');
    if (!el) return;
    const cr = c.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    c.scrollTop = Math.max(0, c.scrollTop + (r.top - cr.top) - (c.clientHeight - r.height) / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------ render: bar
  const exportUrl = api.transcriptExportSrtUrl(transcriptId, exportOpts);
  const cleanupTitle = 'Claude re-splits every line by sentence and speaker and translates';
  const translateTitle = entitled
    ? 'Fill in the missing English lines'
    : 'Translating is part of the paid layer';

  const exportMenu = video.visibility === 'owner' && (
    <Menu closeOnItemClick={false} position="bottom-end" width={220} shadow="md">
      <Menu.Target>
        <BarButton title="Download this transcript as .srt">Export SRT</BarButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => setExportOpts((o) => ({ ...o, speakers: !o.speakers }))}>
          {exportOpts.speakers ? '✓ ' : ''}Speaker prefixes
        </Menu.Item>
        <Menu.Item onClick={() => setExportOpts((o) => ({ ...o, english: !o.english }))}>
          {exportOpts.english ? '✓ ' : ''}English lines
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item closeMenuOnClick component="a" href={exportUrl} target="_blank" rel="noopener">
          Download .srt
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const bar = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        minHeight: 44,
        padding: '4px 12px',
        borderBottom: '1px solid var(--nn-hairline)',
        flexShrink: 0,
      }}
    >
      <Text fz={12.5} fw={600} style={{ whiteSpace: 'nowrap' }}>
        Editing
        <span style={{ color: 'var(--nn-muted)', fontWeight: 400 }}>
          {' '}
          · {lines.length} lines · rev {revision}
        </span>
      </Text>
      {desktop && (
        <Text
          fz={11}
          c="dimmed"
          style={{ fontVariantNumeric: 'tabular-nums', marginLeft: 6, whiteSpace: 'nowrap' }}
        >
          playhead {fmtTimeMs(props.currentMs).slice(0, -2)}
        </Text>
      )}
      <span style={{ flex: 1 }} />
      <BarButton
        onClick={doUndo}
        disabled={disabled || busy || undoReason !== null}
        title={
          disabledReason ??
          undoReason ??
          (undo.length ? describeUndo(undo[undo.length - 1]) : undefined)
        }
      >
        Undo{undoCount > 0 ? ` (${undoCount})` : ''}
      </BarButton>
      {desktop ? (
        <>
          <BarButton
            onClick={() => setConfirmCleanup((c) => !c)}
            disabled={disabled || lines.length === 0}
            title={disabledReason ?? cleanupTitle}
          >
            Run cleanup
          </BarButton>
          {missing > 0 && (
            <BarButton
              onClick={() => onStartJob('translate')}
              disabled={disabled || !entitled}
              title={disabledReason ?? translateTitle}
            >
              Translate missing ({missing})
            </BarButton>
          )}
          <BarButton
            onClick={() => setSnapOpen((o) => !o)}
            title="Earlier versions of this transcript"
          >
            {snapOpen ? '▾ Snapshots' : 'Snapshots'}
          </BarButton>
          {exportMenu}
        </>
      ) : (
        <>
          {missing > 0 && (
            <BarButton
              onClick={() => onStartJob('translate')}
              disabled={disabled || !entitled}
              title={disabledReason ?? translateTitle}
            >
              Translate ({missing})
            </BarButton>
          )}
          <Menu position="bottom-end" width={200} shadow="md">
            <Menu.Target>
              <UnstyledButton
                aria-label="More"
                style={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--nn-muted)',
                }}
              >
                <IconMore />
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                disabled={disabled || lines.length === 0}
                onClick={() => setConfirmCleanup(true)}
              >
                Run cleanup…
              </Menu.Item>
              <Menu.Item onClick={() => setSnapOpen((o) => !o)}>Snapshots</Menu.Item>
              {video.visibility === 'owner' && (
                <Menu.Item component="a" href={exportUrl} target="_blank" rel="noopener">
                  Download .srt
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </>
      )}
      <Button size="compact-sm" onClick={onExit} ml={4}>
        Done
      </Button>
    </div>
  );

  const notices = (
    <>
      {confirmCleanup && (
        <InlineLine>
          <span>
            Cleanup replaces all {lines.length} lines with Claude’s re-split (a snapshot is taken
            first).
          </span>
          <Button
            size="compact-xs"
            onClick={() => {
              setConfirmCleanup(false);
              onStartJob('cleanup');
            }}
          >
            Run cleanup
          </Button>
          <Button size="compact-xs" variant="subtle" onClick={() => setConfirmCleanup(false)}>
            Cancel
          </Button>
        </InlineLine>
      )}
      {snapOpen && (
        <SnapshotList
          transcriptId={transcriptId}
          disabled={disabled}
          disabledReason={disabledReason}
          onRestore={restore}
          onClose={() => setSnapOpen(false)}
        />
      )}
      {banner && (
        <InlineLine role="status">
          <span>
            {jobNoun(banner.jobKind)}
            {banner.jobKind ? ' finished' : ' changed the transcript'} — reload the transcript and
            discard your unsaved edit?
          </span>
          <Button size="compact-xs" onClick={bannerReload}>
            Reload
          </Button>
          <Button size="compact-xs" variant="subtle" onClick={bannerKeep}>
            Keep editing
          </Button>
        </InlineLine>
      )}
      {locked && !banner && (
        <InlineLine role="status">
          <span>{jobLockLine(lockingJob)}</span>
        </InlineLine>
      )}
      {!canEdit && (
        <InlineLine>
          <span>{disabledReason}</span>
        </InlineLine>
      )}
      {status && (
        <InlineLine role="status">
          <span>{status}</span>
        </InlineLine>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, ...style }}>
      {bar}
      {notices}
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <div
          ref={containerRef}
          role="list"
          onContextMenu={(e) => {
            const t = e.target as HTMLElement;
            if (t.tagName !== 'TEXTAREA' && t.tagName !== 'INPUT') e.preventDefault();
          }}
          style={{
            position: 'absolute',
            inset: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(30vh + env(safe-area-inset-bottom))',
          }}
        >
          {lines.map((line, idx) => (
            <LineEditor
              key={line.id}
              line={line}
              idx={idx}
              active={line.id === activeId}
              expanded={line.id === expandedId}
              desktop={desktop}
              canMerge={canMergeAt(lines, idx)}
              disabled={disabled}
              disabledReason={disabledReason}
              speakerListId={speakerListId}
              busy={busyId === line.id}
              error={rowError && rowError.lineId === line.id ? rowError.message : null}
              note={rowNote && rowNote.lineId === line.id ? rowNote.message : null}
              resetKey={resetKey}
              onExpand={onExpand}
              onCollapse={onCollapse}
              onSeek={onSeek}
              onDirty={onDirty}
              onCommit={onCommit}
              onNudge={onNudge}
              onSetToPlayhead={onSetToPlayhead}
              onSplit={onSplit}
              onMerge={onMerge}
              onInsertAfter={onInsertAfter}
              onDelete={onDelete}
            />
          ))}
          {lines.length === 0 && (
            <div style={{ padding: 24, color: 'var(--nn-muted)', fontSize: 14 }}>
              No transcript lines yet.
            </div>
          )}
        </div>
      </div>
      <datalist id={speakerListId}>
        {speakers.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  );
}

// ---------------------------------------------------------------- pieces

function InlineLine({ children, role }: { children: React.ReactNode; role?: React.AriaRole }) {
  return (
    <div
      role={role}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        padding: '6px 12px',
        fontSize: 12.5,
        color: 'var(--nn-muted)',
        background: 'var(--nn-panel-2)',
        borderBottom: '1px solid var(--nn-hairline)',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

const BarButton = forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    title?: string;
  }
>(function BarButton({ children, onClick, disabled, title, ...rest }, ref) {
  return (
    <UnstyledButton
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      title={title}
      {...rest}
      style={{
        minHeight: 32,
        padding: '0 10px',
        borderRadius: 7,
        border: '1px solid var(--nn-hairline)',
        fontSize: 11.5,
        color: 'var(--nn-text)',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </UnstyledButton>
  );
});

function SnapshotList({
  transcriptId,
  disabled,
  disabledReason,
  onRestore,
  onClose,
}: {
  transcriptId: string;
  disabled: boolean;
  disabledReason: string | null;
  onRestore: (snapshotId: string) => void;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['snapshots', transcriptId],
    queryFn: () => api.transcriptSnapshots(transcriptId),
  });
  const [armed, setArmed] = useState<string | null>(null);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(null), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <div
      style={{
        maxHeight: 240,
        overflowY: 'auto',
        borderBottom: '1px solid var(--nn-hairline)',
        background: 'var(--nn-panel)',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 12px' }}>
        <Text fz={11} c="dimmed">
          Snapshots — taken before cleanup and restores
        </Text>
        <span style={{ flex: 1 }} />
        <UnstyledButton
          onClick={onClose}
          aria-label="Close snapshots"
          style={{ color: 'var(--nn-faint)', width: 32, height: 32, textAlign: 'center' }}
        >
          ✕
        </UnstyledButton>
      </div>
      {q.isPending && (
        <div style={{ padding: '4px 12px 10px' }}>
          <Skeleton height={14} width="60%" mb={8} />
          <Skeleton height={14} width="40%" />
        </div>
      )}
      {q.isError && (
        <Text fz={12.5} c="dimmed" px={12} py={4}>
          Couldn’t load snapshots.
        </Text>
      )}
      {q.data && q.data.length === 0 && (
        <Text fz={12.5} c="dimmed" px={12} py={4}>
          No snapshots yet.
        </Text>
      )}
      {q.data?.map((s) => (
        <div
          key={s.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            minHeight: 40,
            borderTop: '1px solid var(--nn-hairline)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text fz={12.5} truncate>
              {s.reason}
              <span style={{ color: 'var(--nn-muted)' }}> · rev {s.revision}</span>
            </Text>
            <Text fz={11} c="dimmed">
              {new Date(s.createdAt).toLocaleString()}
            </Text>
          </div>
          <BarButton
            disabled={disabled}
            title={
              disabledReason ?? (armed === s.id ? 'tap again to restore' : 'restore this version')
            }
            onClick={() => {
              if (armed === s.id) {
                setArmed(null);
                onRestore(s.id);
              } else setArmed(s.id);
            }}
          >
            {armed === s.id ? 'Restore? (tap again)' : 'Restore'}
          </BarButton>
        </div>
      ))}
    </div>
  );
}
